import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import SEO from '../components/SEO';
import OptimizedImage from '../components/OptimizedImage';
import DownloadButton from '../components/DownloadButton';
import './Gratitude.css';

// Final Update styled components
const FinalUpdateContainer = styled(motion.div)`
  background: linear-gradient(135deg, #f0fff4 0%, #c6f6d5 100%);
  border: 2px solid #38a169;
  border-radius: 16px;
  padding: 2rem;
  margin: 2rem auto 3rem auto;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  max-width: 1200px;
`;

const FinalUpdateHeader = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const FinalUpdateTitle = styled.h2`
  color: #2f855a;
  margin-bottom: 0.5rem;
  font-size: 1.8rem;
`;

const FinalUpdateSubtitle = styled.p`
  color: #38a169;
  font-style: italic;
  margin-bottom: 0;
`;

const FinalUpdateContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
`;

const FinalUpdateText = styled.p`
  color: #2d3748;
  line-height: 1.8;
  font-size: 1.05rem;
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FinalUpdateSignature = styled.div`
  text-align: right;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
  color: #4a5568;
  font-style: italic;
`;

// Modal styling components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const ModalContent = styled.div`
  position: relative;
  max-width: 95vw;
  max-height: 95vh;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  
  .image-gallery {
    background: #f8f9fa;
    max-height: 95vh;
  }
  
  .image-gallery-slide {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 70vh;
    overflow: hidden;
  }
  
  .image-gallery-image {
    border-radius: 8px;
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
    display: block;
  }

  .image-gallery-description {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 8px 16px;
    border-radius: 20px;
    fontSize: 14px;
    max-width: 90%;
    text-align: center;
    line-height: 1.4;
    z-index: 1;
  }

  .image-gallery-thumbnails-wrapper {
    background: #f1f3f4;
    padding: 10px 0;
  }

  .image-gallery-thumbnail {
    border: 2px solid transparent;
    border-radius: 4px;
    transition: all 0.2s ease;
  }

  .image-gallery-thumbnail.active,
  .image-gallery-thumbnail:hover {
    border-color: #007bff;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
  z-index: 10001;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }
`;

const PhotoCard = styled.div`
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-4px);
  }
`;

const NextSectionContainer = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 2rem;
  margin: 3rem auto 2rem auto;
  text-align: center;
  max-width: 600px;
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
`;

const NextSectionButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  text-decoration: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  }
`;

const Gratitude = () => {
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Photo data for the gallery
  const photoData = [
    {
      original: '/uploads/hospital-journey/optimized/IMG_7707.jpeg',
      thumbnail: '/uploads/hospital-journey/optimized/IMG_7707.jpeg',
      description: 'Surrounded by love and medical expertise',
      originalAlt: 'Hospital support and care',
      thumbnailAlt: 'Hospital support and care',
      filename: 'IMG_7707.jpeg'
    },
    {
      original: '/uploads/hospital-journey/optimized/IMG_7715.jpeg',
      thumbnail: '/uploads/hospital-journey/optimized/IMG_7715.jpeg',
      description: 'Family unity through the miracle',
      originalAlt: 'Family together during recovery',
      thumbnailAlt: 'Family together during recovery',
      filename: 'IMG_7715.jpeg'
    },
    {
      original: '/uploads/hospital-journey/optimized/IMG_7722.jpeg',
      thumbnail: '/uploads/hospital-journey/optimized/IMG_7722.jpeg',
      description: 'Every moment a blessing',
      originalAlt: 'Moments of hope and healing',
      thumbnailAlt: 'Moments of hope and healing',
      filename: 'IMG_7722.jpeg'
    },
    {
      original: '/uploads/hospital-journey/optimized/IMG_7733.jpeg',
      thumbnail: '/uploads/hospital-journey/optimized/IMG_7733.jpeg',
      description: 'Grateful hearts and healing hands',
      originalAlt: 'Recovery and gratitude',
      thumbnailAlt: 'Recovery and gratitude',
      filename: 'IMG_7733.jpeg'
    },
    {
      original: '/uploads/hospital-journey/graditude_card.jpeg',
      thumbnail: '/uploads/hospital-journey/graditude_card.jpeg',
      description: 'Beautiful expressions of love and gratitude from our community angels',
      originalAlt: 'Thank you card from community supporters',
      thumbnailAlt: 'Thank you card from community supporters',
      filename: 'graditude_card.jpeg'
    },
    {
      original: '/uploads/Andrew_ICU_MountainPoint.jpeg',
      thumbnail: '/uploads/Andrew_ICU_MountainPoint.jpeg',
      description: 'Our amazing ICU nurse Andrew at Holy Cross Hospital - Mountain Point',
      originalAlt: 'Andrew, ICU nurse at Holy Cross Hospital - Mountain Point',
      thumbnailAlt: 'Andrew, ICU nurse at Holy Cross Hospital - Mountain Point',
      filename: 'Andrew_ICU_MountainPoint.jpeg'
    }
  ];

  // Modal handlers
  const openModal = (index) => {
    setCurrentImageIndex(index);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isModalOpen]);

  const gratitudeGroups = [
    {
      title: "Our Children - Heroes at Home",
      description: "Our own children who responded with courage, love, and selfless action during those critical first hours",
      people: [
        {
          name: "Declan (Son-in-law)",
          role: "22-year-old Son-in-law & Support System",
          gratitude: "For immediately stepping up alongside Isabel to manage everything at home, helping transport the kids to and from the hospital, and being the steady support our family needed during the crisis. Your calm presence and practical help kept our family functioning when everything felt like it was falling apart, and for adding humor and stability when we needed it most.",
          support: ["House Management", "Transportation", "Family Support", "Crisis Management"]
        },
        {
          name: "Isabel Soward",
          role: "22-year-old Daughter & Family Manager",
          gratitude: "For taking care of everything needful at the house and bringing the kids to and from the hospital—you were our family's lifeline. When chaos erupted, you stepped in as the family coordinator, managing the household, caring for your siblings, and ensuring everyone could visit Mom when they needed to.",
          support: ["House Management", "Transportation", "Family Care", "Crisis Coordination"]
        },
        {
          name: "Isaac Soward",
          role: "21-year-old Son & Emergency Coordinator",
          gratitude: "For immediately calling 911 when you saw Mom collapse, yelling for Eliza to come help with CPR, guiding the first responders to Mom when they arrived by shouting 'She's in here!', and helping coordinate the emergency response. Your quick thinking and clear communication were crucial in those first critical minutes.",
          support: ["911 Call", "Emergency Coordination", "First Responder Guidance"]
        },
        {
          name: "Noah Soward",
          role: "18-year-old Son & Prayer Warrior",
          gratitude: "For immediately, with genuine heartache and real concern, sending this text message to hundreds of people: 'Please pray for my mom, we think she just had a heart attack.' Your instant prayer mobilization efforts created a network of faith that stretched across states. Thank you also for giving Eliza the shirt off your back when she needed it during CPR, and for coordinating prayers with the younger kids downstairs.",
          support: ["Prayer Mobilization", "Spiritual Support", "Crisis Assistance", "Prayer Coordination"]
        },
        {
          name: "Eliza Jane Soward",
          role: "17-year-old Daughter & Life Saver",
          gratitude: "For rushing downstairs in a towel when called, immediately taking over CPR from Scott, and performing continuous chest compressions for nearly 5 minutes until EMTs arrived. Your recent CPR recertification just days before was divine preparation. You literally saved Mom's life with your courage, training, and quick action.",
          support: ["Life-Saving CPR", "Medical Response", "Crisis Leadership"]
        },
        {
          name: "Anna Soward",
          role: "12-year-old Daughter",
          gratitude: "For your brave response during this scary time, helping care for your younger sisters, and joining in prayer with your siblings downstairs. Your strength, maturity, and faith helped keep the family together when everything felt like it was falling apart.",
          support: ["Family Support", "Sibling Care", "Prayer Support", "Emotional Strength"]
        },
        {
          name: "Violet Soward",
          role: "9-year-old Daughter",
          gratitude: "For being so brave during the scariest day of your young life and joining with your siblings in prayer downstairs. Your courage, faith, and the way you supported your family, even when you didn't understand everything that was happening, showed incredible strength for such a little person.",
          support: ["Brave Response", "Family Unity", "Prayer Support", "Emotional Courage"]
        },
        {
          name: "Sophia Soward",
          role: "6-year-old Daughter",
          gratitude: "For being our littlest helper and bringing smiles even in the darkest moments, and for joining your brothers and sisters in prayer downstairs. Your innocent joy, loving heart, and faithful prayers reminded us what we were fighting for and helped hold our family together.",
          support: ["Joy & Light", "Family Comfort", "Prayer Support", "Loving Presence"]
        }
      ]
    },
    // {
    //   title: "Family & Extended Family",
    //   description: "Our beloved family members who rallied around us with love and support",
    //   people: [
    //     {
    //       name: "Ingrid Pinto",
    //       role: "Sister",
    //       gratitude: "For bringing food, visiting multiple times, and being the first to see and talk to Maggie when she woke up—your presence meant everything",
    //       support: ["Hospital Visits", "Provided Food"]
    //     },
    //     {
    //       name: "Gabriel Pinto",
    //       role: "Brother-in-law (Ingrid's husband)",
    //       gratitude: "For taking time to visit and show your support during our family's crisis",
    //       support: ["Hospital Visits"]
    //     },
    //     {
    //       name: "Ben Lambert",
    //       role: "Brother-in-law (Lee's husband)",
    //       gratitude: "For visiting, helping with priesthood blessings, and giving Scott timely and key advice when we needed guidance most",
    //       support: ["Hospital Visits", "Gave Blessing", "Spiritual Counsel"]
    //     },
    //     {
    //       name: "Liesl Lambert",
    //       role: "Sister",
    //       gratitude: "For your loving visits, being part of our family support system during this miracle, and for sending Maggie a megaphone to help her communicate and advocate for herself during recovery",
    //       support: ["Hospital Visits", "Thoughtful Gift", "Recovery Support"]
    //     },
    //     {
    //       name: "Isabel & Declan",
    //       role: "Daughter and Son-in-law",
    //       gratitude: "For taking care of everything needful at the house and bringing the kids to and from the hospital—you were our family's lifeline",
    //       support: ["House Management", "Transportation", "Family Care"]
    //     },
    //     {
    //       name: "Bethany McConkie",
    //       role: "Sister-in-law",
    //       gratitude: "For visiting and giving wise counsel during our time of need—your friendship and guidance were invaluable",
    //       support: ["Hospital Visits", "Spiritual Counsel"]
    //     },
    //     {
    //       name: "Brit McConkie",
    //       role: "Brother-in-law",
    //       gratitude: "For taking time to visit and show your care and support for our family",
    //       support: ["Hospital Visits"]
    //     },
    //     {
    //       name: "Sarah Brown",
    //       role: "Aunt (Maggie's Sister)",
    //       gratitude: "For bringing us delicious Cafe Rio meals at the hospital, providing comfort food when we needed nourishment and couldn't think about cooking—your thoughtfulness sustained us",
    //       support: ["Hospital Visits", "Provided Food", "Family Care"]
    //     },
    //     {
    //       name: "Tamara and David McCullough",
    //       role: "Friends & Family",
    //       gratitude: "For bringing us a thoughtful gift basket and a beautiful card during our recovery time, showing your love and support when we needed encouragement most",
    //       support: ["Thoughtful Gift", "Family Support", "Encouragement"]
    //     },
    //     {
    //       name: "Becca Lish",
    //       role: "Cousin (Maggie's Family)",
    //       gratitude: "For writing beautiful cards to both Eliza and Maggie and sending them books to read during recovery—your thoughtful gifts provided comfort and inspiration during healing",
    //       support: ["Thoughtful Gift", "Recovery Support", "Family Care", "Encouragement"]
    //     }
    //   ]
    // },
    // {
    //   title: "Spiritual Leaders & Church Family",
    //   description: "Our faith community who provided spiritual strength and divine support",
    //   people: [
    //     {
    //       name: "Clint Nelson",
    //       role: "Bishop",
    //       gratitude: "For being the first to arrive at the ER, meeting us there, and giving sacred priesthood blessings to both Scott and Maggie—your spiritual leadership anchored us",
    //       support: ["Hospital Visits", "Gave Blessing", "First Responder"]
    //     },
    //     {
    //       name: "Lindi Nelson",
    //       role: "Bishop's Wife & Friend",
    //       gratitude: "For visiting and bringing Scott an amazing Thanksgiving dinner when normal life felt impossible—your thoughtfulness sustained us",
    //       support: ["Hospital Visits", "Provided Food"]
    //     },
    //     {
    //       name: "Lindi Nelson, Crystal Barclay & Allison Stout",
    //       role: "Dear Friends & Neighbors",
    //       gratitude: "For visiting Maggie in the hospital, covering for Maggie in the ward primary during her recovery, and bringing a beautiful poster with all the primary children's signatures and messages of love and support—your friendship and service meant everything",
    //       support: ["Hospital Visits", "Community Love", "Service Coverage", "Primary Support"]
    //     },
    //     {
    //       name: "Spring Meadow Ward",
    //       role: "Our Faith Community",
    //       gratitude: "For coming together as a unified body of Christ to help organize and support Noah's farewell luncheon at the clubhouse, showing love and fellowship as our family celebrated this important milestone during our recovery journey",
    //       support: ["Community Service", "Farewell Support", "Ward Fellowship", "Family Celebration"]
    //     }
    //   ]
    // },
    // {
    //   title: "Guardian Angels & Childcare Heroes",
    //   description: "The incredible friends who stepped up to care for our children in our moment of greatest need",
    //   people: [
    //     {
    //       name: "Heather Jordan",
    //       role: "Friend & Neighbor",
    //       gratitude: "For immediately coordinating and caring for our little children while Maggie was being rushed to the hospital—you became their safe harbor",
    //       support: ["Emergency Childcare", "Crisis Coordination"]
    //     },
    //     {
    //       name: "Tiffany Bryant",
    //       role: "Friend & Neighbor",
    //       gratitude: "For coordinating and caring for our little children during the chaos—your quick response was a miracle in itself",
    //       support: ["Emergency Childcare", "Crisis Coordination"]
    //     },
    //     {
    //       name: "Kirsten Hall",
    //       role: "Friend & Neighbor",
    //       gratitude: "For coordinating and caring for our little children while we faced the unknown—your love surrounded them when we couldn't",
    //       support: ["Emergency Childcare", "Crisis Coordination"]
    //     },
    //     {
    //       name: "Allison Provenza",
    //       role: "Friend",
    //       gratitude: "For taking our kids while Maggie was in the hospital and for days after—giving us peace of mind knowing they were loved and cared for",
    //       support: ["Extended Childcare", "Family Support"]
    //     },
    //     {
    //       name: "Rachel Luke",
    //       role: "Neighbor & Guardian Angel",
    //       gratitude: "For taking care of Violet and Sophia at your house for 2 full days, providing them with dinner, love, and a safe haven during our family's crisis—you became their home away from home when we couldn't be there",
    //       support: ["Extended Childcare", "Provided Food", "Family Care", "Emergency Support"]
    //     },
    //     {
    //       name: "Steve and Melene Hauck",
    //       role: "Neighbors & Friends",
    //       gratitude: "For bringing delicious pasta to our family during our recovery time, providing nourishing comfort food when we needed it most",
    //       support: ["Provided Food", "Neighbor Support", "Family Care"]
    //     },
    //     {
    //       name: "April Jacob",
    //       role: "Neighbor & Transportation Hero",
    //       gratitude: "For helping transport our children to and from school during our family crisis, ensuring their routines continued while we focused on Maggie's recovery",
    //       support: ["Transportation", "School Support", "Neighbor Care", "Family Support"]
    //     }
    //   ]
    // },
    // {
    //   title: "Medical Angels & Healthcare Heroes",
    //   description: "The dedicated professionals who provided expert care during Maggie's recovery",
    //   people: [
    //     {
    //       name: "EMTs, Firefighters, and Police Officers",
    //       role: "First Responders & Life Savers",
    //       gratitude: "For arriving quickly when we needed you most and immediately taking over life-saving CPR and medical care. Your professional expertise, calm presence, and skilled hands continued the miracle that Eliza started. You carried Maggie from our home to the ambulance with such care and precision, and your rapid response and expert medical intervention were absolutely crucial in saving her life.",
    //       support: ["Emergency Medical Response", "Life-Saving CPR", "Professional Care", "Rapid Response"]
    //     },
    //     {
    //       name: "Andrew Bennett",
    //       role: "ICU Nurse at Holy Cross Hospital",
    //       gratitude: "For providing amazing care during Maggie's most critical hours—your skill and compassion were part of the miracle",
    //       support: ["Medical Care", "Professional Excellence"]
    //     }
    //   ]
    // },
    // {
    //   title: "Additional Angels & Community Supporters",
    //   description: "More dear friends and neighbors who blessed our family with their love, service, and support during our time of greatest need",
    //   people: [
    //     {
    //       name: "Mark and Sabrina Grotefend",
    //       role: "Friends & Community Angels",
    //       gratitude: "For your love, support, and caring hearts that reached out to our family during our time of need—your kindness was a blessing",
    //       support: ["Community Love", "Family Support"]
    //     },
    //     {
    //       name: "Chris and Whitney Allen",
    //       role: "Friends & Community Supporters",
    //       gratitude: "For your thoughtful care and support that helped sustain our family during the miracle—your love meant everything",
    //       support: ["Community Love", "Family Support"]
    //     },
    //     {
    //       name: "Ken and Barbara Holt",
    //       role: "Friends & Caring Hearts",
    //       gratitude: "For your love, prayers, and support that surrounded our family during this miraculous time—your caring hearts were felt deeply",
    //       support: ["Community Love", "Prayer Support", "Family Care"]
    //     },
    //     {
    //       name: "Tom and Christi Walsh",
    //       role: "Friends & Community Supporters",
    //       gratitude: "For reaching out with love and support during our family's crisis—your kindness was a source of strength",
    //       support: ["Community Love", "Family Support"]
    //     },
    //     {
    //       name: "Jason and Katie Cowden",
    //       role: "Friends & Generous Hearts",
    //       gratitude: "For catering delicious Cafe Rio food and sharing messages of love and support that nourished our family during the miracle—your generosity was deeply appreciated",
    //       support: ["Provided Food", "Community Love", "Family Support"]
    //     },
    //     {
    //       name: "Chandler and Desirae Smoot",
    //       role: "Friends & Community Angels",
    //       gratitude: "For your love, support, and caring presence that blessed our family during our time of greatest need",
    //       support: ["Community Love", "Family Support"]
    //     },
    //     {
    //       name: "Grant and Carly Peterson",
    //       role: "Friends & Supporters",
    //       gratitude: "For your thoughtful care and support that helped sustain our family during the miracle—your kindness was a blessing",
    //       support: ["Community Love", "Family Support"]
    //     },
    //     {
    //       name: "Troy and Bonni Card",
    //       role: "Friends & Community Hearts",
    //       gratitude: "For your love, prayers, and support that surrounded our family during this miraculous time—your caring hearts touched ours",
    //       support: ["Community Love", "Prayer Support", "Family Care"]
    //     },
    //     {
    //       name: "Michael and Emily Raines",
    //       role: "Friends & Caring Supporters",
    //       gratitude: "For reaching out with love and support during our family's crisis—your kindness was a source of comfort",
    //       support: ["Community Love", "Family Support"]
    //     },
    //     {
    //       name: "Steve and Colleen Olson",
    //       role: "Friends & Community Angels",
    //       gratitude: "For your thoughtful care and support that helped lift our family during the miracle—your love was deeply felt",
    //       support: ["Community Love", "Family Support"]
    //     },
    //     {
    //       name: "Josh and Jen Neilson",
    //       role: "Friends & Supporters",
    //       gratitude: "For your love, prayers, and caring hearts that reached out to our family during our time of need—your kindness was a blessing",
    //       support: ["Community Love", "Prayer Support", "Family Care"]
    //     },
    //     {
    //       name: "Scott and Tristine Chappell",
    //       role: "Friends & Community Hearts",
    //       gratitude: "For your thoughtful care and support that helped sustain our family during the miracle—your love meant everything",
    //       support: ["Community Love", "Family Support"]
    //     },
    //     {
    //       name: "Mike and Carolyn Melis",
    //       role: "Friends & Service Angels",
    //       gratitude: "For bringing dinner to Scott and Maggie and taking the time to visit Maggie in the hospital—your personal care and thoughtful service meant so much during our recovery",
    //       support: ["Provided Meals", "Hospital Visits", "Personal Care"]
    //     },
    //     {
    //       name: "Jen Muirbrook",
    //       role: "Friend & Creative Angel",
    //       gratitude: "For making a beautiful Christmas tree with pictures of our family—your thoughtful creativity brought joy and warmth to our home during the holiday season",
    //       support: ["Thoughtful Gift", "Creative Love", "Family Joy"]
    //     },
    //     {
    //       name: "Debbie and Jake",
    //       role: "Cousin & Family Support (Maggie's Cousin)",
    //       gratitude: "For sending money for meals during our recovery time—your generous heart and practical support helped nourish our family when we needed it most",
    //       support: ["Generous Gift", "Provided Food", "Family Support"]
    //     },
    //     {
    //       name: "Sean and Shauna Treseder",
    //       role: "Friends & Caring Hearts",
    //       gratitude: "For sending a beautiful care package that brought comfort and love to our family during our time of healing—your thoughtfulness was deeply appreciated",
    //       support: ["Care Package", "Community Love", "Family Support"]
    //     },
    //     {
    //       name: "Paul and Susan Miller",
    //       role: "Friends & Flower Angels",
    //       gratitude: "For sending beautiful flowers that brightened our days and reminded us of the love surrounding our family during recovery",
    //       support: ["Sent Flowers", "Community Love", "Encouragement"]
    //     },
    //     {
    //       name: "Brittany Shipley, Michelle Mortimer & Joanna Tew",
    //       role: "Friends & Hospital Angels",
    //       gratitude: "For visiting Maggie in the hospital and giving a beautiful gift basket filled with love and care. Joanna, though you couldn't make it, your heart was with us and your thoughtful gift meant everything—all three of you blessed our family",
    //       support: ["Hospital Visits", "Thoughtful Gift", "Community Love", "Care Basket"]
    //     },
    //     {
    //       name: "Ewen Fitzgerald",
    //       role: "Friend & Flower Angel",
    //       gratitude: "For giving Maggie a beautiful bouquet of flowers that brought brightness and beauty to her recovery—your thoughtful gesture touched our hearts",
    //       support: ["Beautiful Flowers", "Community Love", "Recovery Support"]
    //     },
    //     {
    //       name: "Adam and Allison Stout",
    //       role: "Friends & Endless Support",
    //       gratitude: "For your endless support, prayers, and service to our family throughout this entire journey—your consistent love and care have been a constant source of strength",
    //       support: ["Endless Support", "Prayer Support", "Service", "Family Care"]
    //     },
    //     {
    //       name: "Steve and Susan Muse",
    //       role: "Friends & Prayer Warriors",
    //       gratitude: "For fasting for Maggie's recovery and our family—your sacrifice and spiritual dedication added power to the prayers ascending on our behalf",
    //       support: ["Fasting", "Prayer Support", "Spiritual Sacrifice", "Family Care"]
    //     },
    //     {
    //       name: "Jonathan and Kirsten",
    //       role: "Friends & Hospital Supporters",
    //       gratitude: "For your prayers and for visiting Maggie in the hospital—your presence and faithful support during our darkest hours meant everything to our family",
    //       support: ["Hospital Visits", "Prayer Support", "Community Love", "Faithful Presence"]
    //     }
    //   ]
    // },
    // {
    //   title: "Extended Family & Distant Angels",
    //   description: "Family members and dear friends from far away who reached out with love and support across the miles",
    //   people: [
    //     {
    //       name: "Mike & Myra Soward, Larry & Debbie Soward, Rachel & Chris Smith, Holly & Nick Wilt, Larry & Randi Soward, and Shawna Jones",
    //       role: "Extended Family from the East",
    //       gratitude: "For your love and kind words and support from Cincinnati, Carlisle, and Clarksville—distance could not diminish the warmth of your hearts or the strength of your love that reached across the miles to comfort our family",
    //       support: ["Long-Distance Love", "Family Support", "Kind Words", "Cross-Country Care"]
    //     },
    //     {
    //       name: "Our Beloved Chilean Brothers and Sisters",
    //       role: "International Family of Faith",
    //       gratitude: "For reaching out from Chile to express your love and support—your messages, prayers, and caring hearts crossed oceans to touch our family. The bonds of friendship and faith know no borders, and your love strengthened us during our greatest trial",
    //       support: ["International Support", "Prayer Support", "Cross-Ocean Love", "Faithful Friendship"]
    //     }
    //   ]
    // },
    // {
    //   title: "Generous Hearts & Ongoing Support",
    //   description: "Friends who blessed our family with incredible generosity and lasting gifts",
    //   people: [
    //     {
    //       name: "Heather Robison",
    //       role: "Friend",
    //       gratitude: "For blessing the Soward family with season pass Splash Summit tickets, bringing dinner to our family, giving Maggie a thoughtful care basket, and taking Maggie out to dinner after she was released from the hospital—your ongoing friendship and support brought joy and healing",
    //       support: ["Generous Gift", "Family Joy", "Provided Food", "Care Basket", "Recovery Support"]
    //     },
    //     {
    //       name: "Jordan Robison",
    //       role: "Friend",
    //       gratitude: "For blessing the Soward family with season pass Splash Summit tickets—your thoughtfulness brought light to difficult days",
    //       support: ["Generous Gift", "Family Joy"]
    //     },
    //     {
    //       name: "Maria Antonieta Gonzalez",
    //       role: "Friend & Generous Heart",
    //       gratitude: "For blessing our family with incredible love, support, and 20 homemade empanadas that filled our home with warmth and comfort during our recovery time",
    //       support: ["Provided Food", "Love & Support", "Generous Gift"]
    //     },
    //     {
    //       name: "Chris and Whitney Allen",
    //       role: "Friends & Caring Neighbors",
    //       gratitude: "For bringing delicious ice cream sundaes and a wonderful dinner to our family, providing comfort food and fellowship when we needed it most",
    //       support: ["Provided Food", "Family Care", "Community Love"]
    //     }
    //   ]
    // }
  ];

  return (
    <div className="gratitude-container">
      <SEO 
        title="Gratitude & Community Support"
        description="Expressing heartfelt gratitude to the countless people who prayed, served, and supported the Soward family during Maggie's medical emergency and recovery. A testament to community love and faith."
        url="/gratitude"
        image="/uploads/hospital-journey/graditude_card.jpeg"
        type="article"
      />
      <div className="gratitude-hero">
        <div className="gratitude-hero-content">
          <h1 className="gratitude-title">Our Hearts Overflow with Gratitude</h1>
          <p className="gratitude-subtitle">
            "Be thankful in all circumstances; for this is the will of God in Christ Jesus for you." - 1 Thessalonians 5:18
          </p>
          <div className="gratitude-intro">
            <p>
              Maggie's event wasn't just a medical success, it was the result of countless angels, 
              both earthly and heavenly, who responded with love, skill, and faith. Our family is 
              forever changed by the outpouring of support, service, and prayer that surrounded us 
              during our darkest hour and brightest miracle.
            </p>
          </div>
        </div>
      </div>

      <div className="gratitude-content">
        <FinalUpdateContainer
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <FinalUpdateHeader>
            <FinalUpdateTitle>Final Update from Scott Soward</FinalUpdateTitle>
            <FinalUpdateSubtitle>Posted after Maggie came home from the hospital</FinalUpdateSubtitle>
          </FinalUpdateHeader>
          
          <FinalUpdateContent>
            <FinalUpdateText>
              Last and hopefully final update on our crazy week. Maggie came home yesterday at 3 o'clock, 
              and it was so wonderful to be with the kids and in our own home. There were paper hearts with 
              messages all over the garage door, front door, chalk drawings on the drive, and flowers and 
              gifts in our home. It was a wonderful welcome home for Maggie and me. ❤️🙏
            </FinalUpdateText>
            
            <FinalUpdateText>
              It is impossible to summarize at this point what we experienced the past seven days, and I 
              suspect we'll need some time to process. But Maggie is doing good, the device they put in her 
              chest is causing her pain as she heals, and she'll also be adjusting to the cocktail of medicines 
              she'll be taking to help her heart recover and get stronger. But she is alive and we are home!
            </FinalUpdateText>
            
            <FinalUpdateText>
              Our time in the hospital produced all the emotions. Day one, we were so scared she would not 
              pull through, and then deeply concerned that she might have brain damage from the lack of blood 
              and oxygen to the brain during the cardiac arrest (what Maggie likes to call her death). Then 
              relieved on Friday when she raised her eyebrows and wiggled her toes on command because we knew 
              she was still in there.
            </FinalUpdateText>
            
            <FinalUpdateText>
              She finally woke up Friday night and talking to her while she was still seriously sedated was 
              hilarious. She could not remember anything from five minutes to the next so she asked the same 
              questions on repeat. Her sisters and all the kids were there. She said the funniest things, 
              flirting with Andrew the nurse and embarrassing Declan. We all laughed and loved her up. She 
              also said the most loving and endearing things, and we shared tears of joy as she told us 
              (repeatedly) of her love for us and her love for Jesus.
            </FinalUpdateText>
            
            <FinalUpdateText>
              We are all recovering, and we'll work together to get through this time. But we are so grateful 
              to all of you who have reached out, and shared your love with us, your time, flowers and gifts, 
              messages, and service taking care of our kids. Please know that it was a great comfort knowing 
              that while Maggie and I were in the hospital, our kids were loved and taken care of. Thank you. ❤️
            </FinalUpdateText>
            
            <FinalUpdateText>
              Also, I cannot express how grateful we are to our loving Heavenly Father who allowed Maggie to 
              stay with us and arranged for the perfect scenario where she was preserved and survived this 
              terrifying experience. We have felt His love and mercy and peace. I am amazed and terrified when 
              I think of how differently the outcome would have been with a minor change to just a small detail 
              of the events on Thanksgiving day. She died and came back to us thanks to Him. We have seen miracles.
            </FinalUpdateText>
            
            <FinalUpdateText>
              And I know there's many things that happened prior to and on that day that we are not aware of 
              that provided for Maggie's rescue. We are grateful for the emergency workers, EMT's firefighters 
              and police, for their training and decision to have an occupation that literally saves and changes 
              lives. We're so grateful to the hospital staff, nurses doctors, cardiologists that have elected 
              to work and to serve their fellow human beings and save lives.
            </FinalUpdateText>
            
            <FinalUpdateText>
              We are grateful for family, for blessings, and the power of prayer. We are so grateful for all 
              of you who have reached out and shared with us your love for Maggie and our family, and provided 
              for us in so many ways. My heart breaks for those of you who have gone through similar situations 
              with less desirable outcomes. I'm so sorry for your pain and your loss. We love you all and wish you peace.
            </FinalUpdateText>
            
            <FinalUpdateText>
              We are all in God's hands and His love for us is poured out through the hands of earthly and 
              heavenly angels. Thank you all!
            </FinalUpdateText>
            
            <FinalUpdateSignature>
              — Scott Soward, on behalf of the Soward Family
            </FinalUpdateSignature>
          </FinalUpdateContent>
        </FinalUpdateContainer>

        <div className="gratitude-photos-section">
          <h2>Moments of Grace and Recovery</h2>
          <p>These images capture the love, support, and healing that surrounded our family</p>
          
          <div className="photo-grid">
            {photoData.map((photo, index) => (
              <PhotoCard key={index} className="photo-card" onClick={() => openModal(index)}>
                <OptimizedImage
                  src={photo.thumbnail || photo.original}
                  alt={photo.originalAlt}
                  thumbnail={true}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <div className="photo-caption">
                  <p>{photo.description}</p>
                </div>
              </PhotoCard>
            ))}
          </div>

          {/* Modal */}
          {isModalOpen && (
            <ModalOverlay onClick={closeModal}>
              <ModalContent onClick={(e) => e.stopPropagation()}>
                <CloseButton onClick={closeModal}>×</CloseButton>
                <ImageGallery
                  items={photoData}
                  startIndex={currentImageIndex}
                  showThumbnails={true}
                  showFullscreenButton={false}
                  showPlayButton={false}
                  slideDuration={300}
                  slideInterval={0}
                  showBullets={false}
                  showNav={true}
                  renderItem={(item) => (
                    <div style={{ textAlign: 'center' }}>
                      <img
                        src={item.original}
                        alt={item.description}
                        style={{
                          maxHeight: '70vh',
                          maxWidth: '100%',
                          borderRadius: '8px'
                        }}
                        onError={(e) => {
                          console.error('Gratitude image failed to load:', e.target.src);
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjE1MCIgeT0iMTAwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiPkltYWdlIEZhaWxlZCB0byBMb2FkPC90ZXh0Pjwvc3ZnPg==';
                        }}
                        onLoad={() => {
                          // Image loaded successfully
                        }}
                      />

                      {/* Download button for images */}
                      {item.filename && (
                        <div style={{ margin: '1rem 0' }}>
                          <DownloadButton
                            category="Gratitude & Community Support"
                            filename={item.filename}
                            caption={item.description}
                            onError={(error) => console.error('Download error:', error)}
                          />
                        </div>
                      )}

                      {item.description && (
                        <div className="image-gallery-description">
                          {item.description}
                        </div>
                      )}
                    </div>
                  )}
                />
              </ModalContent>
            </ModalOverlay>
          )}
        </div>

        {gratitudeGroups.map((group, groupIndex) => (
          <div key={groupIndex} className="gratitude-group">
            <div className="group-header">
              <h2 className="group-title">{group.title}</h2>
              <p className="group-description">{group.description}</p>
            </div>
            
            <div className="gratitude-grid">
              {group.people.map((person, personIndex) => (
                <div key={personIndex} className="gratitude-card">
                  <div className="card-content">
                    <div className="person-info">
                      <h3 className="person-name">{person.name}</h3>
                      <p className="person-role">{person.role}</p>
                      {person.support && (
                        <div className="support-badges">
                          {person.support.map((supportType, index) => (
                            <span key={index} className="support-badge">{supportType}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="gratitude-message-text">
                      <p>"{person.gratitude}"</p>
                    </div>
                  </div>
                  <div className="card-accent"></div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="special-recognition">
          {/* <div className="noah-section">
            <h2>Noah's Prayer Call to the World</h2>
            <div className="noah-story">
              <div className="text-message-box">
                <div className="phone-header">
                  <span className="phone-time">12:23 PM</span>
                  <span className="phone-carrier">••• Verizon</span>
                  <span className="phone-battery">🔋 100%</span>
                </div>
                <div className="message-content">
                  <p className="message-text">
                    "Please pray for my mom, we think she just had a heart attack"
                  </p>
                  <div className="message-info">
                    <span className="sender">Noah Soward</span>
                    <span className="recipients">To: Hundreds of contacts</span>
                  </div>
                </div>
              </div>
              <div className="noah-description">
                <p>
                  In the midst of chaos and fear, 18-year-old Noah demonstrated remarkable faith and 
                  maturity. With genuine heartache and real concern, he began instantly sending this 
                  text message to hundreds of people. His immediate efforts to mobilize prayers for 
                  his mother made a significant impact in calling down heaven's help during our 
                  greatest hour of need.
                </p>
                <p>
                  <em>
                    While medical hands worked to save Maggie's life, Noah's hands worked to gather 
                    the prayers of hundreds—creating a network of faith that stretched across states 
                    and touched heaven itself.
                  </em>
                </p>
              </div>
            </div>
          </div> */}

          <div className="community-response">
            <h2>A Community United</h2>
            <div className="community-content">
              <p>
                Beyond those specifically named above, we are overwhelmed by the hundreds of people 
                who responded to Noah's call for prayer and rallied around our family. This miracle 
                was made possible by:
              </p>
              <div className="community-grid">
                <div className="community-category">
                  <h3>Neighbors</h3>
                  <p>Who opened their homes and hearts to our children</p>
                </div>
                <div className="community-category">
                  <h3>Family</h3>
                  <p>Who traveled great distances to be by our side</p>
                </div>
                <div className="community-category">
                  <h3>Friends</h3>
                  <p>Across the states and around the world who prayed without ceasing</p>
                </div>
                <div className="community-category">
                  <h3>Ward Members</h3>
                  <p>Who fasted, prayed, and served with Christ-like love</p>
                </div>
              </div>
              {/* <div className="community-actions">
                <h3>Acts of Service That Surrounded Us:</h3>
                <div className="action-list">
                  <span className="action-item">Texting words of comfort and hope</span>
                  <span className="action-item">Fasting and praying for Maggie's recovery</span>
                  <span className="action-item">Bringing meals to nourish our family</span>
                  <span className="action-item">Cleaning our house when we couldn't</span>
                  <span className="action-item">Taking care of our children with love</span>
                  <span className="action-item">Sending flowers to brighten our days</span>
                  <span className="action-item">Countless other acts of pure love</span>
                </div>
              </div> */}
              <div className="community-quote">
                <blockquote>
                  "Though we cannot name each of you individually, please know that every prayer offered, 
                  every meal brought, every child cared for, every tear shed, and every act of service 
                  became part of the miracle that brought Maggie back to us. You are all written in our 
                  hearts forever."
                </blockquote>
                <div className="signature">
              <p><em>With love and gratitude,</em></p>
              <p><strong>The Soward Family</strong></p>
              <p>Scott, Maggie, Declan, Isabel, Isaac, Atziri, Noah, Eliza, Anna, Violet, and Sophia</p>
            </div>
              </div>
            </div>
          </div>
        </div>

        {/* <div className="community-response-section">
          <div className="community-card">
            <h2>The Hundreds Who Answered the Call</h2>
            <div className="community-content">
              <p>
                Beyond those we've named individually, we are overwhelmed by the hundreds of 
                neighbors, family members, friends across the states and around the world, and 
                ward members who reached out in our time of need.
              </p>
              <div className="community-actions">
                <div className="action-grid">
                  <div className="action-item">
                    
                    <h3>Texting & Praying</h3>
                    <p>Countless messages of love, concern, and promises of prayer</p>
                  </div>
                  <div className="action-item">
                    
                    <h3>Fasting</h3>
                    <p>Many fasted and prayed for Maggie's recovery</p>
                  </div>
                  <div className="action-item">
                    
                    <h3>Bringing Food</h3>
                    <p>Meals provided when cooking felt impossible</p>
                  </div>
                  <div className="action-item">
                    
                    <h3>Cleaning Our House</h3>
                    <p>Keeping our home running while we focused on Maggie</p>
                  </div>
                  <div className="action-item">
                    
                    <h3>Taking Care of Our Kids</h3>
                    <p>Watching over our children with love and care</p>
                  </div>
                  <div className="action-item">
                    
                    <h3>Sending Flowers</h3>
                    <p>Beautiful reminders of love and hope</p>
                  </div>
                </div>
              </div>
              <div className="community-impact">
                <p>
                  <strong>To every person who sent a text, offered a prayer, brought a meal, 
                  cleaned our home, watched our children, or sent flowers—</strong> you became 
                  part of Maggie's miracle. Your love created a network of support that lifted 
                  our entire family and proved that miracles happen through the combined faith 
                  and service of God's children.
                </p>
                <p>
                  <em>We may not know all your names, but God does, and so do our hearts.</em>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="gratitude-footer">
          <div className="footer-content">
            <h2>A Miracle Made Possible by Many</h2>
            <p>
              Maggie's 6% chance of survival became 100% reality because of each of you. 
              In our hour of greatest need, you answered the call. You became the hands and 
              feet of Christ, the embodiment of love in action.
            </p>
            <p>
              Our family will forever carry your kindness in our hearts. This miracle belongs 
              to all of us—a testament to what happens when communities unite in love, faith, 
              and service.
            </p>
            <div className="signature">
              <p><em>With endless love and gratitude,</em></p>
              <p><strong>The Soward Family</strong></p>
              <p>Scott, Maggie, Declan, Isabel, Isaac, Atziri, Noah, Eliza, Anna, Violet, and Sophia</p>
            </div>
          </div>
        </div> */}

        <NextSectionContainer>
          {/* <NextSectionText>
            Continue exploring Maggie's miracle story with Eliza's Hero Award ceremony...
          </NextSectionText> */}
          <NextSectionButton href="/elizas-award">
            View Eliza's Hero Award →
          </NextSectionButton>
        </NextSectionContainer>
      </div>
    </div>
  );
};

export default Gratitude;
